/**
 * INSERT — place a suggestion where the drafter put the caret.
 *
 * Placement is the one judgment a drafter makes instantly and this system
 * cannot. Every rung of the lp-006 routing ladder exists to guess a location; a
 * cursor is an oracle. INSERT does not replace the guess — nobody wants to place
 * twenty-six suggestions by hand — it means the guess only has to be good rather
 * than correct.
 *
 * The sequence is rebuild → accept → delete, in that order:
 *
 *   - Rebuilding at the new anchor rather than relocating the existing marker
 *     keeps every step on a path the editor already runs. Relocating means
 *     hand-editing live review state (`diff-pid`, the diffgram item's `_pid`,
 *     the annotation's container, the list bookkeeping), and getting any of it
 *     subtly wrong corrupts numbering or cross-references silently.
 *   - Deleting the original last is failure-safe. Removing it first would leave
 *     the drafter with nothing if the rebuild failed.
 *   - Deleting rather than rejecting keeps the audit trail honest. The drafter
 *     moved the suggestion; they did not decide against it, and recording a
 *     rejection would say they had.
 */

/**
 * Reasons INSERT can refuse, and what the drafter can do about each.
 *
 * `remedy` splits them into the two kinds, which want different offers:
 *
 *   - `move_caret` — the drafter clicked in the wrong place. One click fixes
 *     it, so say where to click.
 *   - `copy` — we cannot place this text anywhere, ever. A block-level
 *     paragraph inside a table cell is structurally impossible for us, and a
 *     form with no paragraph style has nothing to pour the block into. Both are
 *     perfectly reasonable things for a drafter to want, so hand them the text
 *     and let them place it themselves. Refusing without an offer would be
 *     telling them their goal is unreachable when only our route to it is.
 *
 * Every message states what happened AND what to do next. Three of these used
 * to only diagnose — "The cursor is outside the lease text." — which leaves a
 * drafter mid-insert to infer the remedy, the one thing she has no spare
 * attention for.
 *
 * `paste` is separate from `message` on the copy-remedy refusals because those
 * have two outcomes and the right instruction differs between them. When the
 * clipboard write lands, telling her to copy is wrong — it already happened.
 * When it is refused, telling her to copy is the whole remedy. Composing in
 * `refusalFlash` is what lets one refusal serve both.
 */
export const INSERT_REFUSALS = Object.freeze({
  no_caret: {
    remedy: 'move_caret',
    message: 'Put the cursor where the change should go.',
  },
  outside_lease: {
    remedy: 'move_caret',
    message:
      'The cursor is outside the lease. Click where the change should go.',
  },
  no_block: {
    remedy: 'move_caret',
    message:
      'This paragraph cannot carry a new one. Put the cursor in body text.',
  },
  in_table: {
    remedy: 'copy',
    message: 'A table cell cannot take a new paragraph.',
    paste: 'paste it into the cell',
  },
  unsupported_list_template: {
    remedy: 'copy',
    message: 'This form has no paragraph style that can carry the new text.',
    paste: 'paste it where it belongs',
  },
  // Not the drafter's doing and not something she can fix by moving the cursor,
  // so it names what broke and what to do about it rather than reporting our
  // defect back to her as a placement problem.
  no_content: {
    remedy: 'move_caret',
    message:
      'This suggestion arrived with no language to place. Reject it and re-run the import.',
  },
});

/**
 * The refusal as the drafter should read it, given whether the clipboard write
 * actually landed.
 *
 * Shared rather than composed at each call site: `insertHere` and
 * `insertRevisedText` build the same string, and the previous version appended
 * " It is on your clipboard." to a message that had just told her to copy.
 */
export function refusalFlash(result, copied) {
  const message = (result && result.message) || '';
  const paste = result && result.paste;

  if (!paste) return message;
  if (copied) return `${message} It is on your clipboard — ${paste}.`;

  return `${message} Copy the text and ${paste}.`;
}

function refuse(reason) {
  const refusal = INSERT_REFUSALS[reason] || {};
  return {
    ok: false,
    reason,
    message: refusal.message,
    // Carried through, or `refusalFlash` never sees it and the copy-remedy
    // refusals silently lose the clause naming where the text goes.
    paste: refusal.paste,
    remedy: refusal.remedy || 'move_caret',
  };
}

function elementFor(node) {
  if (!node) return null;
  return node.nodeType === 3 ? node.parentElement : node;
}

/**
 * Resolve a caret position to the block a new paragraph should follow.
 *
 * A caret mid-paragraph resolves to the whole paragraph rather than splitting
 * it. Clausebook's `insertClause` branches on `isCaretAtEndContainer` and splits
 * the block; a suggestion is a paragraph, so splitting a sentence around it
 * would be a different edit than the one the drafter accepted.
 */
export function anchorBlockForCaret(range, root) {
  if (!range || !root) return refuse('no_caret');

  const element = elementFor(range.startContainer);
  if (!element) return refuse('no_caret');
  if (!root.contains(element)) return refuse('outside_lease');

  const block = element.closest('[pid]');
  if (!block || !root.contains(block)) return refuse('no_block');

  // The gap left open when `anchor_not_trailing` was retired: a block-level
  // insertion inside a <td> is structurally wrong, and the list-item builder
  // assumes body-level flow. Refusing here is the guard that rule used to
  // provide by accident.
  if (block.closest('td, th')) return refuse('in_table');

  return { ok: true, anchor: block };
}

/**
 * Retire a generated request without recording a decision about it.
 *
 * Deliberately not `_reject`: that path marks the item rejected and resolved,
 * which is a statement about what the drafter wanted. Moving a suggestion is
 * not deciding against it.
 *
 * The diffgram slot is emptied rather than spliced out. `diff-index` attributes
 * across the document address this array positionally, so removing an element
 * would renumber every later request and point live markers at the wrong item.
 */
export function removeGeneratedRequest(diffgram, annotations, item) {
  if (!diffgram || !Array.isArray(diffgram.items) || !item) return false;

  const index = item._index;
  if (diffgram.items[index] !== item) return false;

  const markers = item._generatedMarkers || [];
  markers.forEach(marker => {
    // An insertion's marker owns the block it added, so removing the marker
    // removes the suggestion. Nothing that existed before it is touched.
    if (marker && marker.parentNode) marker.remove();
  });

  diffgram.items[index] = undefined;

  // By `diff-index`, not by node identity. `diffgram.actual` is what the review
  // counter measures, and createGeneratedAnnotations builds it from a detached
  // `<dummy>` holding the card's sanitized snapshot — so every entry is a CLONE
  // of a document marker and `includes()` never matches the live nodes this
  // function is given.
  //
  // Nothing was retired, so INSERT HERE only ever added: the replacement
  // arrived, the original stayed in the count as a blocked request that can
  // never resolve, and "48 changes" became a number the drafter could not reach.
  // Identity is still checked first, for the callers that do hold live markers.
  if (Array.isArray(diffgram.actual)) {
    const indexAttribute = String(index);
    diffgram.actual = diffgram.actual.filter(entry => {
      if (markers.includes(entry)) return false;
      return (
        !entry ||
        typeof entry.getAttribute !== 'function' ||
        entry.getAttribute('diff-index') !== indexAttribute
      );
    });
  }

  if (annotations && item._annotation) {
    Object.keys(annotations).forEach(key => {
      if (annotations[key] === item._annotation) delete annotations[key];
    });
  }

  return true;
}

/**
 * What INSERT should place for a request.
 *
 * A drafting note is itself an insertion under lp-005, so the naive answer
 * re-anchors and accepts the note text — committing "[DRAFTING NOTE — …]" into
 * the lease as permanent prose, which is the opposite of the intent. The clause
 * the refused proposal wanted is carried on the placement as
 * `insertableContent` (document_note_placement.js).
 */
export function contentToInsert(item) {
  if (!item) return '';

  const placement = item._notePlacement;
  if (placement && String(placement.insertableContent || '').trim()) {
    return placement.insertableContent;
  }

  return (item.proposal && item.proposal.content) || '';
}

/**
 * The suggestion's text, as plain prose fit for the clipboard.
 *
 * `contentToInsert` is already plain — model output assigned through
 * textContent, never markup — but this is the value that leaves the
 * application, and the neighbouring source of "the text of a suggestion" is the
 * annotation card's html, which is full of `ins` and `del` review markers.
 * Anyone reaching for that by mistake would paste review scaffolding into a
 * lease, so the strip is here rather than assumed.
 */
export function plainTextForCopy(item) {
  const raw = contentToInsert(item);
  if (!raw) return '';

  const host = document.createElement('div');
  host.innerHTML = String(raw);
  host.querySelectorAll('del').forEach(element => element.remove());
  host.querySelectorAll('ins').forEach(element => {
    element.replaceWith(...element.childNodes);
  });

  return host.textContent.replace(/\s+/g, ' ').trim();
}

/**
 * Whether the INSERT action applies to a request at all.
 *
 * Only where there is a placement question. A text_patch's wording is either in
 * the lease or it is not; deletions, row operations and field conflicts have no
 * location to choose. Offering INSERT there would need a second meaning for it.
 */
export function canInsertHere(item) {
  if (!item || !item.proposal) return false;
  if (item._notePlacement) return true;

  return item.proposal.kind === 'insert_block';
}
